import React, { useEffect, useState } from 'react';
import { contractsAPI, invoicesAPI } from './api';

const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmt = (n) =>
  amount(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatMoney = (value, currency = 'INR') => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount(value));
  } catch {
    return `${currency || 'INR'} ${fmt(value)}`;
  }
};

const formatDate = (value, fallback = '—') => {
  if (!value) return fallback;
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateInput = (value) => (value ? String(value).split('T')[0] : '');
const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const STATUS_META = {
  active: { label: 'Active', color: 'cst-active' },
  draft: { label: 'Draft', color: 'cst-draft' },
  completed: { label: 'Completed', color: 'cst-completed' },
  expired: { label: 'Expired', color: 'cst-expired' },
  terminated: { label: 'Terminated', color: 'cst-terminated' },
};

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'CAD', 'AUD'];
const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Singapore',
  'Canada', 'Australia', 'Germany', 'France', 'Netherlands', 'Japan', 'Other',
];

const CONTRACT_TYPES = [
  ['lump_sum', 'Lump Sum'],
  ['time_based', 'Time-Based'],
  ['framework', 'Framework'],
  ['retainer', 'Retainer'],
  ['mixed', 'Mixed'],
  ['other', 'Other'],
];

const DONOR_OPTIONS = [
  ['', 'Select Donor / Financier'],
  ['World Bank', 'World Bank'],
  ['Asian Development Bank (ADB)', 'Asian Development Bank (ADB)'],
  ['UNICEF', 'UNICEF'],
  ['UNDP', 'UNDP'],
  ['World Health Organization (WHO)', 'World Health Organization (WHO)'],
  ['European Union', 'European Union'],
  ['JICA', 'JICA'],
  ['KfW', 'KfW'],
  ['GIZ', 'GIZ'],
  ['USAID', 'USAID'],
  ['FCDO', 'FCDO'],
  ['Bill & Melinda Gates Foundation', 'Bill & Melinda Gates Foundation'],
  ['Other', 'Other'],
];

const SELECTION_METHODS = ['QCBS', 'QBS', 'CQS', 'FBS', 'LCS', 'IC', 'Direct', 'Other'];
const MILESTONE_STATUSES = ['pending', 'in_review', 'approved', 'invoiced', 'paid', 'delayed', 'cancelled'];
const DELIVERABLE_STATUSES = ['draft', 'in_progress', 'submitted', 'revision_requested', 'accepted', 'rejected'];
const DOCUMENT_STATUSES = ['draft', 'submitted', 'approved', 'archived'];
const AMENDMENT_STATUSES = ['draft', 'pending_approval', 'approved', 'rejected'];
const AMENDMENT_TYPES = [
  ['scope_change', 'Scope Change'],
  ['time_extension', 'Time Extension'],
  ['fee_revision', 'Fee Revision'],
  ['no_cost_extension', 'No-Cost Extension'],
  ['variation', 'Variation'],
  ['other', 'Other'],
];

const EMPTY_FORM = {
  contract_number: '',
  project_name: '',
  description: '',
  donor_name: '',
  donor_program: '',
  funding_reference: '',
  project_reference: '',
  executing_agency: '',
  implementing_agency: '',
  selection_method: 'QCBS',
  contract_type: 'lump_sum',
  client_name: '',
  client_address: '',
  client_gst: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  start_date: '',
  end_date: '',
  contract_value: '',
  status: 'active',
  client_country: 'India',
  billing_currency: 'INR',
  apply_gst: true,
};

const PHASE_EMPTY = {
  milestones: 'No milestones added yet.',
  deliverables: 'No deliverables added yet.',
  documents: 'No contract documents registered yet.',
  amendments: 'No amendments or variations added yet.',
};

const STATUS_TONE = {
  approved: 'success',
  paid: 'success',
  accepted: 'success',
  completed: 'success',
  invoiced: 'info',
  in_review: 'info',
  submitted: 'info',
  pending_approval: 'warning',
  delayed: 'danger',
  rejected: 'danger',
  cancelled: 'danger',
  revision_requested: 'warning',
};

const ContractInput = ({ label, value, onChange, type = 'text', placeholder = '', required, step }) => (
  <div className="cp-fg">
    <label className="cp-lbl">{label}{required && <span className="cp-req">*</span>}</label>
    <input
      className="cp-inp"
      type={type}
      required={required}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      step={step}
    />
  </div>
);

const ContractTextarea = ({ label, value, onChange, rows = 3, placeholder = '' }) => (
  <div className="cp-fg">
    <label className="cp-lbl">{label}</label>
    <textarea
      className="cp-inp cp-ta"
      rows={rows}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

const ContractSelect = ({ label, value, onChange, options }) => (
  <div className="cp-fg">
    <label className="cp-lbl">{label}</label>
    <select className="cp-inp cp-select" value={value || ''} onChange={e => onChange(e.target.value)}>
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>{optionLabel}</option>
      ))}
    </select>
  </div>
);

const PhaseStatusChip = ({ status }) => (
  <span className={`cd-phase-status ${STATUS_TONE[status] || 'neutral'}`}>
    {humanize(status)}
  </span>
);

const withCurrentOption = (options, currentValue) => {
  if (!currentValue || options.some(([value]) => value === currentValue)) return options;
  return [...options.slice(0, -1), [currentValue, `${currentValue} (Existing)`], options[options.length - 1]];
};

const compactText = (value, max = 56) => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  return normalized.length > max ? `${normalized.slice(0, max - 1)}…` : normalized;
};

const normalizeComparableText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const matchesInvoiceToContract = (invoice, contract) => {
  if (!invoice || !contract) return false;
  const invoiceContractId = invoice.contractId || invoice.contract_id || null;
  if (String(invoiceContractId || '') === String(contract.id || '')) return true;

  const invoiceAgreement = normalizeComparableText(invoice.agreementNumber || invoice.agreement_number);
  const contractNumber = normalizeComparableText(contract.contract_number);
  const invoiceClient = normalizeComparableText(invoice.clientName || invoice.client_name);
  const contractClient = normalizeComparableText(contract.client_name);

  if (!invoiceContractId && contractNumber && invoiceAgreement && invoiceClient && contractClient) {
    const agreementMatch = invoiceAgreement === contractNumber
      || invoiceAgreement.includes(contractNumber)
      || contractNumber.includes(invoiceAgreement);
    const clientMatch = invoiceClient === contractClient
      || invoiceClient.includes(contractClient)
      || contractClient.includes(invoiceClient);
    if (agreementMatch && clientMatch) return true;
  }

  return false;
};

const invoiceItemOptionsForContract = (contract) => {
  const invoices = Array.isArray(contract?.invoices) ? contract.invoices : [];
  return invoices.flatMap(inv => {
    const items = Array.isArray(inv.items) ? inv.items : [];
    return items
      .filter(item => String(item?.description || '').trim())
      .map((item, index) => ({
        value: `${inv.id}:${index}`,
        invoiceNumber: inv.invoice_number || inv.invoiceNumber,
        title: compactText(String(item.description).split('\n')[0], 60),
        description: String(item.description || '').trim(),
        amount: item.amount || '',
        label: `${inv.invoice_number || inv.invoiceNumber} · ${compactText(item.description, 52)}${item.amount ? ` · ${item.amount}` : ''}`,
      }));
  });
};

function buildPhaseForm(type, initial, contractCurrency) {
  if (type === 'milestones') {
    return {
      title: initial?.title || '',
      description: initial?.description || '',
      target_date: toDateInput(initial?.target_date),
      amount: initial?.amount ?? '',
      status: initial?.status || 'pending',
      acceptance_date: toDateInput(initial?.acceptance_date),
      currency: contractCurrency || 'INR',
    };
  }

  if (type === 'deliverables') {
    return {
      milestone_id: initial?.milestone_id ? String(initial.milestone_id) : '',
      title: initial?.title || '',
      description: initial?.description || '',
      due_date: toDateInput(initial?.due_date),
      submitted_date: toDateInput(initial?.submitted_date),
      accepted_date: toDateInput(initial?.accepted_date),
      reviewer_name: initial?.reviewer_name || '',
      status: initial?.status || 'draft',
      revision_count: initial?.revision_count ?? 0,
      external_url: initial?.external_url || '',
      notes: initial?.notes || '',
    };
  }

  if (type === 'documents') {
    return {
      title: initial?.title || '',
      doc_type: initial?.doc_type || '',
      version: initial?.version || '',
      document_date: toDateInput(initial?.document_date),
      external_url: initial?.external_url || '',
      notes: initial?.notes || '',
      is_required: !!initial?.is_required,
      status: initial?.status || 'draft',
    };
  }

  return {
    amendment_number: initial?.amendment_number || '',
    amendment_type: initial?.amendment_type || 'other',
    summary: initial?.summary || '',
    effective_date: toDateInput(initial?.effective_date),
    value_delta: initial?.value_delta ?? '',
    days_delta: initial?.days_delta ?? 0,
    approval_date: toDateInput(initial?.approval_date),
    status: initial?.status || 'draft',
    currency: contractCurrency || 'INR',
  };
}

function buildPhasePayload(type, form) {
  if (type === 'milestones') {
    return {
      title: form.title,
      description: form.description,
      target_date: form.target_date,
      amount: parseFloat(form.amount) || 0,
      status: form.status,
      acceptance_date: form.acceptance_date,
    };
  }

  if (type === 'deliverables') {
    return {
      milestone_id: form.milestone_id || null,
      title: form.title,
      description: form.description,
      due_date: form.due_date,
      submitted_date: form.submitted_date,
      accepted_date: form.accepted_date,
      reviewer_name: form.reviewer_name,
      status: form.status,
      revision_count: parseInt(form.revision_count, 10) || 0,
      external_url: form.external_url,
      notes: form.notes,
    };
  }

  if (type === 'documents') {
    return {
      title: form.title,
      doc_type: form.doc_type,
      version: form.version,
      document_date: form.document_date,
      external_url: form.external_url,
      notes: form.notes,
      is_required: !!form.is_required,
      status: form.status,
    };
  }

  return {
    amendment_number: form.amendment_number,
    amendment_type: form.amendment_type,
    summary: form.summary,
    effective_date: form.effective_date,
    value_delta: parseFloat(form.value_delta) || 0,
    days_delta: parseInt(form.days_delta, 10) || 0,
    approval_date: form.approval_date,
    status: form.status,
  };
}

function ContractPanel({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial ? {
    ...EMPTY_FORM,
    ...initial,
    start_date: toDateInput(initial.start_date),
    end_date: toDateInput(initial.end_date),
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const donorOptions = withCurrentOption(DONOR_OPTIONS, form.donor_name);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { ...form, contract_value: parseFloat(form.contract_value) || 0 };
      const result = initial?.id
        ? await contractsAPI.update(initial.id, payload)
        : await contractsAPI.create(payload);
      onSave(result, !!initial?.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCountryChange = (country) => {
    set('client_country', country);
    if (country !== 'India') {
      set('apply_gst', false);
      const currMap = {
        'United States': 'USD',
        'United Kingdom': 'GBP',
        'United Arab Emirates': 'AED',
        Singapore: 'SGD',
        Canada: 'CAD',
        Australia: 'AUD',
        Germany: 'EUR',
        France: 'EUR',
        Netherlands: 'EUR',
      };
      if (currMap[country]) set('billing_currency', currMap[country]);
    } else {
      set('apply_gst', true);
      set('billing_currency', 'INR');
    }
  };

  return (
    <div className="cp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cp-panel">
        <div className="cp-header">
          <div>
            <h2 className="cp-title">{initial?.id ? 'Edit Contract' : 'New Contract'}</h2>
            <p className="cp-sub">Define client, donor context, timeline, value, and procurement metadata</p>
          </div>
          <button className="cp-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cp-body">
          <div className="cp-section-title">Project Details</div>
          <div className="cp-grid2">
            <ContractInput
              label="Project / Work Name"
              value={form.project_name}
              onChange={v => set('project_name', v)}
              required
              placeholder="e.g. Urban Resilience TA"
            />
            <ContractInput
              label="Contract / Agreement Number"
              value={form.contract_number}
              onChange={v => set('contract_number', v)}
              placeholder="e.g. CS/QCBS/2026/04"
            />
          </div>
          <ContractTextarea
            label="Description / Scope"
            value={form.description}
            onChange={v => set('description', v)}
            rows={2}
            placeholder="Brief scope of work"
          />

          <div className="cp-section-title">Donor &amp; Procurement</div>
          <div className="cp-grid2">
            <ContractSelect
              label="Donor / Financier"
              value={form.donor_name}
              onChange={v => set('donor_name', v)}
              options={donorOptions}
            />
            <ContractInput
              label="Program / Facility"
              value={form.donor_program}
              onChange={v => set('donor_program', v)}
              placeholder="TA facility / loan program"
            />
            <ContractInput
              label="Funding Reference"
              value={form.funding_reference}
              onChange={v => set('funding_reference', v)}
              placeholder="Loan / Grant / TA number"
            />
            <ContractInput
              label="Project / Package Reference"
              value={form.project_reference}
              onChange={v => set('project_reference', v)}
              placeholder="Project ID / package / CSRN"
            />
            <ContractInput
              label="Executing Agency"
              value={form.executing_agency}
              onChange={v => set('executing_agency', v)}
              placeholder="Client ministry / agency"
            />
            <ContractInput
              label="Implementing Agency"
              value={form.implementing_agency}
              onChange={v => set('implementing_agency', v)}
              placeholder="PIU / department / local body"
            />
            <ContractSelect
              label="Selection Method"
              value={form.selection_method}
              onChange={v => set('selection_method', v)}
              options={SELECTION_METHODS.map(v => [v, v])}
            />
            <ContractSelect
              label="Contract Type"
              value={form.contract_type}
              onChange={v => set('contract_type', v)}
              options={CONTRACT_TYPES}
            />
          </div>

          <div className="cp-section-title">Client Details</div>
          <ContractInput
            label="Client Name"
            value={form.client_name}
            onChange={v => set('client_name', v)}
            required
            placeholder="Organization name"
          />
          <ContractTextarea
            label="Client Address"
            value={form.client_address}
            onChange={v => set('client_address', v)}
            rows={3}
            placeholder="Full postal address"
          />
          <div className="cp-grid2">
            <ContractInput
              label="Client GST Number"
              value={form.client_gst}
              onChange={v => set('client_gst', v)}
              placeholder="e.g. 06AAJCT2216R1ZX"
            />
            <ContractInput
              label="Kind Attention (Contact Person)"
              value={form.contact_person}
              onChange={v => set('contact_person', v)}
              placeholder="Name / Designation"
            />
            <ContractInput
              label="Contact Email"
              value={form.contact_email}
              onChange={v => set('contact_email', v)}
              type="email"
              placeholder="contact@client.org"
            />
            <ContractInput
              label="Contact Phone"
              value={form.contact_phone}
              onChange={v => set('contact_phone', v)}
              placeholder="+91-XXXXXXXXXX"
            />
          </div>

          <div className="cp-section-title">Billing &amp; Tax</div>
          <div className="cp-grid2">
            <div className="cp-fg">
              <label className="cp-lbl">Client Country</label>
              <select className="cp-inp cp-select" value={form.client_country || 'India'} onChange={e => handleCountryChange(e.target.value)}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="cp-fg">
              <label className="cp-lbl">Billing Currency</label>
              <select className="cp-inp cp-select" value={form.billing_currency || 'INR'} onChange={e => set('billing_currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <label className="cp-checkbox-label">
            <input type="checkbox" checked={!!form.apply_gst} onChange={e => set('apply_gst', e.target.checked)} />
            Apply GST on invoices
            {form.client_country && form.client_country !== 'India' && (
              <span className="cp-gst-note">(international client - GST typically not applicable)</span>
            )}
          </label>

          <div className="cp-section-title">Timeline &amp; Value</div>
          <div className="cp-grid2">
            <ContractInput label="Start Date" value={form.start_date} onChange={v => set('start_date', v)} type="date" />
            <ContractInput label="End Date" value={form.end_date} onChange={v => set('end_date', v)} type="date" />
            <ContractInput
              label={`Base Contract Value (${form.billing_currency || 'INR'})`}
              value={form.contract_value}
              onChange={v => set('contract_value', v)}
              type="number"
              step="0.01"
              placeholder="0.00"
            />
            <div className="cp-fg">
              <label className="cp-lbl">Status</label>
              <select className="cp-inp cp-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          {error && <div className="cp-error">{error}</div>}

          <div className="cp-footer">
            <button type="button" className="cp-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="cp-save" disabled={saving}>
              {saving ? 'Saving...' : initial?.id ? 'Update Contract' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PhaseRecordModal({ contract, type, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => buildPhaseForm(type, initial, contract.billing_currency || 'INR'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invoicePrefillKey, setInvoicePrefillKey] = useState('');
  const isEdit = !!initial?.id;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const invoiceItemOptions = invoiceItemOptionsForContract(contract);

  const titleMap = {
    milestones: ['Milestone', 'Milestones'],
    deliverables: ['Deliverable', 'Deliverables'],
    documents: ['Document', 'Documents'],
    amendments: ['Amendment', 'Amendments'],
  };

  const applyInvoicePrefill = (value) => {
    setInvoicePrefillKey(value);
    const selected = invoiceItemOptions.find(item => item.value === value);
    if (!selected) return;

    if (type === 'milestones') {
      setForm(p => ({
        ...p,
        title: p.title || selected.title || `Milestone for ${selected.invoiceNumber}`,
        description: p.description || selected.description,
        amount: p.amount || selected.amount || '',
      }));
      return;
    }

    if (type === 'deliverables') {
      setForm(p => ({
        ...p,
        title: p.title || selected.title || `Deliverable for ${selected.invoiceNumber}`,
        description: p.description || selected.description,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = buildPhasePayload(type, form);
      if (isEdit) {
        await contractsAPI.updateItem(contract.id, type, initial.id, payload);
      } else {
        await contractsAPI.createItem(contract.id, type, payload);
      }
      onSaved();
    } catch (err) {
      setError(
        err.message === 'Route not found'
          ? `${titleMap[type][0]} route is not available on the running backend. Restart the backend server and try again.`
          : err.message
      );
    } finally {
      setSaving(false);
    }
  };

  const renderFields = () => {
    if (type === 'milestones') {
      return (
        <>
          {!isEdit && invoiceItemOptions.length > 0 && (
            <div className="cp-fg">
              <label className="cp-lbl">Prefill from Existing Invoice Item</label>
              <select className="cp-inp cp-select" value={invoicePrefillKey} onChange={e => applyInvoicePrefill(e.target.value)}>
                <option value="">Select invoice item</option>
                {invoiceItemOptions.map(item => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>
          )}
          <ContractInput label="Milestone Title" value={form.title} onChange={v => set('title', v)} required placeholder="e.g. Inception approved" />
          <ContractTextarea label="Description" value={form.description} onChange={v => set('description', v)} rows={3} placeholder="Scope or acceptance notes" />
          <div className="modal-grid2">
            <ContractInput label="Target Date" value={form.target_date} onChange={v => set('target_date', v)} type="date" />
            <ContractInput label={`Milestone Value (${form.currency})`} value={form.amount} onChange={v => set('amount', v)} type="number" step="0.01" placeholder="0.00" />
            <ContractSelect label="Status" value={form.status} onChange={v => set('status', v)} options={MILESTONE_STATUSES.map(v => [v, humanize(v)])} />
            <ContractInput label="Acceptance Date" value={form.acceptance_date} onChange={v => set('acceptance_date', v)} type="date" />
          </div>
        </>
      );
    }

    if (type === 'deliverables') {
      return (
        <>
          {!isEdit && invoiceItemOptions.length > 0 && (
            <div className="cp-fg">
              <label className="cp-lbl">Prefill from Existing Invoice Item</label>
              <select className="cp-inp cp-select" value={invoicePrefillKey} onChange={e => applyInvoicePrefill(e.target.value)}>
                <option value="">Select invoice item</option>
                {invoiceItemOptions.map(item => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="modal-grid2">
            <div className="cp-fg">
              <label className="cp-lbl">Linked Milestone</label>
              <select className="cp-inp cp-select" value={form.milestone_id} onChange={e => set('milestone_id', e.target.value)}>
                <option value="">No milestone link</option>
                {(contract.milestones || []).map(item => (
                  <option key={item.id} value={item.id}>{item.title}</option>
                ))}
              </select>
            </div>
            <ContractSelect label="Status" value={form.status} onChange={v => set('status', v)} options={DELIVERABLE_STATUSES.map(v => [v, humanize(v)])} />
          </div>
          <ContractInput label="Deliverable Title" value={form.title} onChange={v => set('title', v)} required placeholder="e.g. Draft diagnostic report" />
          <ContractTextarea label="Description" value={form.description} onChange={v => set('description', v)} rows={3} placeholder="Short summary of output" />
          <div className="modal-grid2">
            <ContractInput label="Due Date" value={form.due_date} onChange={v => set('due_date', v)} type="date" />
            <ContractInput label="Reviewer / Approver" value={form.reviewer_name} onChange={v => set('reviewer_name', v)} placeholder="Client focal point" />
            <ContractInput label="Submitted Date" value={form.submitted_date} onChange={v => set('submitted_date', v)} type="date" />
            <ContractInput label="Accepted Date" value={form.accepted_date} onChange={v => set('accepted_date', v)} type="date" />
            <ContractInput label="Revision Count" value={form.revision_count} onChange={v => set('revision_count', v)} type="number" placeholder="0" />
            <ContractInput label="Reference Link" value={form.external_url} onChange={v => set('external_url', v)} type="url" placeholder="Drive / SharePoint / URL" />
          </div>
          <ContractTextarea label="Notes" value={form.notes} onChange={v => set('notes', v)} rows={2} placeholder="Comments, action points, evidence links" />
        </>
      );
    }

    if (type === 'documents') {
      return (
        <>
          <div className="modal-grid2">
            <ContractInput label="Document Title" value={form.title} onChange={v => set('title', v)} required placeholder="e.g. Signed contract" />
            <ContractInput label="Document Type" value={form.doc_type} onChange={v => set('doc_type', v)} placeholder="Contract / TOR / Deliverable / Approval" />
            <ContractInput label="Version" value={form.version} onChange={v => set('version', v)} placeholder="v1.0 / Final" />
            <ContractInput label="Document Date" value={form.document_date} onChange={v => set('document_date', v)} type="date" />
            <ContractInput label="Reference Link" value={form.external_url} onChange={v => set('external_url', v)} type="url" placeholder="Drive / SharePoint / URL" />
            <ContractSelect label="Status" value={form.status} onChange={v => set('status', v)} options={DOCUMENT_STATUSES.map(v => [v, humanize(v)])} />
          </div>
          <label className="cp-checkbox-label">
            <input type="checkbox" checked={!!form.is_required} onChange={e => set('is_required', e.target.checked)} />
            Required for audit / donor compliance
          </label>
          <ContractTextarea label="Notes" value={form.notes} onChange={v => set('notes', v)} rows={3} placeholder="What this document covers, where the signed copy sits, remarks" />
        </>
      );
    }

    return (
      <>
        <div className="modal-grid2">
          <ContractInput label="Amendment Number" value={form.amendment_number} onChange={v => set('amendment_number', v)} placeholder="e.g. Amend. 02" />
          <ContractSelect label="Amendment Type" value={form.amendment_type} onChange={v => set('amendment_type', v)} options={AMENDMENT_TYPES} />
        </div>
        <ContractTextarea label="Summary" value={form.summary} onChange={v => set('summary', v)} rows={3} placeholder="Describe the approved change, extension, or fee revision" />
        <div className="modal-grid2">
          <ContractInput label="Effective Date" value={form.effective_date} onChange={v => set('effective_date', v)} type="date" />
          <ContractSelect label="Status" value={form.status} onChange={v => set('status', v)} options={AMENDMENT_STATUSES.map(v => [v, humanize(v)])} />
          <ContractInput label={`Value Delta (${form.currency})`} value={form.value_delta} onChange={v => set('value_delta', v)} type="number" step="0.01" placeholder="0.00" />
          <ContractInput label="Extension Days" value={form.days_delta} onChange={v => set('days_delta', v)} type="number" placeholder="0" />
          <ContractInput label="Approval Date" value={form.approval_date} onChange={v => set('approval_date', v)} type="date" />
        </div>
      </>
    );
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box contract-sub-modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">{isEdit ? `Edit ${titleMap[type][0]}` : `Add ${titleMap[type][0]}`}</div>
            <div className="modal-inv-ref">{contract.project_name}</div>
          </div>
          <button className="cp-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body contract-sub-body">
            {renderFields()}
            {error && <div className="modal-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContractDetail({ contract, onClose, onNewInvoice, onEdit, onReload, onLoadInvoice }) {
  const contractCurrency = contract.billing_currency || 'INR';
  const invoiced = amount(contract.invoiced_amount);
  const baseValue = amount(contract.contract_value);
  const approvedVariation = amount(contract.approved_amendment_value);
  const currentValue = amount(contract.current_contract_value) || baseValue + approvedVariation;
  const remaining = currentValue - invoiced;
  const pct = currentValue > 0 ? Math.min((invoiced / currentValue) * 100, 100) : 0;
  const isExpiring = contract.end_date && new Date(contract.end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && contract.status === 'active';
  const revisedEndDate = contract.revised_end_date ? formatDate(contract.revised_end_date) : (contract.end_date ? formatDate(contract.end_date) : 'Ongoing');
  const milestones = contract.milestones || [];
  const deliverables = contract.deliverables || [];
  const documents = contract.documents || [];
  const amendments = contract.amendments || [];
  const [editor, setEditor] = useState(null);
  const [sectionError, setSectionError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const handleOpenInvoice = (invoice) => {
    if (!onLoadInvoice) return;
    onClose();
    onLoadInvoice(invoice, contract);
  };

  const snapshot = [
    { label: 'Milestones', value: `${milestones.filter(x => ['approved', 'invoiced', 'paid'].includes(x.status)).length}/${milestones.length}`, hint: 'approved / total' },
    { label: 'Deliverables', value: `${deliverables.filter(x => x.status === 'accepted').length}/${deliverables.length}`, hint: 'accepted / total' },
    { label: 'Documents', value: `${documents.filter(x => x.status === 'approved').length}/${documents.length}`, hint: 'approved / total' },
    { label: 'Amendments', value: `${amendments.filter(x => x.status === 'approved').length}/${amendments.length}`, hint: 'approved / total' },
  ];

  const handleDeleteItem = async (type, itemId, label) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    setBusyKey(`${type}:${itemId}`);
    setSectionError('');
    try {
      await contractsAPI.removeItem(contract.id, type, itemId);
      await onReload(contract.id);
    } catch (err) {
      setSectionError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const renderPhaseActions = (type, item, label) => (
    <div className="cd-phase-actions">
      <button className="cd-phase-act edit" onClick={() => setEditor({ type, initial: item })}>Edit</button>
      <button
        className="cd-phase-act delete"
        disabled={busyKey === `${type}:${item.id}`}
        onClick={() => handleDeleteItem(type, item.id, label)}
      >
        {busyKey === `${type}:${item.id}` ? '...' : 'Delete'}
      </button>
    </div>
  );

  return (
    <>
      <div className="cp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="cp-panel">
          <div className="cp-header">
            <div>
              <h2 className="cp-title">{contract.project_name}</h2>
              <p className="cp-sub">{contract.client_name}</p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="cd-edit-btn" onClick={onEdit}>Edit</button>
              <button className="cp-close" onClick={onClose}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="cp-body">
            <div className="cd-meta-row">
              <span className={`cst-badge ${STATUS_META[contract.status]?.color || 'cst-draft'}`}>
                {STATUS_META[contract.status]?.label || contract.status}
              </span>
              {isExpiring && <span className="cst-badge cst-expiring">Expiring Soon</span>}
              {contract.contract_number && <span className="cd-meta-chip">#{contract.contract_number}</span>}
              {contract.donor_name && <span className="cd-meta-chip">{contract.donor_name}</span>}
              {contract.contract_type && <span className="cd-meta-chip">{humanize(contract.contract_type)}</span>}
              {(contract.start_date || contract.end_date) && (
                <span className="cd-meta-chip">
                  {contract.start_date ? formatDate(contract.start_date) : 'Start TBD'}
                  {' -> '}
                  {revisedEndDate}
                </span>
              )}
            </div>

            <div className="cd-value-block">
              <div className="cd-value-row">
                <div className="cd-val-item">
                  <div className="cd-val-label">Base Value</div>
                  <div className="cd-val-num">{formatMoney(baseValue, contractCurrency)}</div>
                </div>
                <div className="cd-val-item">
                  <div className={`cd-val-num ${approvedVariation < 0 ? 'overrun' : 'invoiced'}`}>{formatMoney(approvedVariation, contractCurrency)}</div>
                  <div className="cd-val-label">Approved Variations</div>
                </div>
                <div className="cd-val-item">
                  <div className="cd-val-label">Current Ceiling</div>
                  <div className="cd-val-num">{formatMoney(currentValue, contractCurrency)}</div>
                </div>
                <div className="cd-val-item">
                  <div className="cd-val-label">Invoiced</div>
                  <div className="cd-val-num invoiced">{formatMoney(invoiced, contractCurrency)}</div>
                </div>
                <div className="cd-val-item">
                  <div className="cd-val-label">Remaining</div>
                  <div className={`cd-val-num ${remaining < 0 ? 'overrun' : 'remaining'}`}>{formatMoney(remaining, contractCurrency)}</div>
                </div>
              </div>
              {currentValue > 0 && (
                <div className="cd-progress-wrap">
                  <div className="cd-progress-bar" style={{ width: `${pct}%` }} />
                  <span className="cd-pct">{pct.toFixed(1)}% invoiced</span>
                </div>
              )}
            </div>

            <div className="cp-section-title">Client Information</div>
            <div className="cd-info-grid">
              {[
                ['Client', contract.client_name],
                ['Attention', contract.contact_person],
                ['Email', contract.contact_email],
                ['Phone', contract.contact_phone],
                ['GST', contract.client_gst],
                ['Country', contract.client_country],
                ['Currency', contract.billing_currency],
              ].filter(([, value]) => value).map(([label, value]) => (
                <div key={label} className="cd-info-row">
                  <span className="cd-info-label">{label}</span>
                  <span className="cd-info-value">{value}</span>
                </div>
              ))}
              {contract.client_address && (
                <div className="cd-info-row cd-span-all">
                  <span className="cd-info-label">Address</span>
                  <span className="cd-info-value" style={{ whiteSpace: 'pre-line' }}>{contract.client_address}</span>
                </div>
              )}
            </div>

            <div className="cp-section-title">Donor &amp; Procurement</div>
            <div className="cd-info-grid">
              {[
                ['Donor', contract.donor_name],
                ['Program', contract.donor_program],
                ['Funding Ref', contract.funding_reference],
                ['Project Ref', contract.project_reference],
                ['Executing Agency', contract.executing_agency],
                ['Implementing Agency', contract.implementing_agency],
                ['Selection Method', contract.selection_method],
                ['Contract Type', humanize(contract.contract_type)],
              ].filter(([, value]) => value).map(([label, value]) => (
                <div key={label} className="cd-info-row">
                  <span className="cd-info-label">{label}</span>
                  <span className="cd-info-value">{value}</span>
                </div>
              ))}
            </div>

            <div className="cp-section-title">Execution Snapshot</div>
            <div className="cd-snapshot-grid">
              {snapshot.map(item => (
                <div key={item.label} className="cd-snapshot-card">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                  <small>{item.hint}</small>
                </div>
              ))}
            </div>

            {sectionError && <div className="cp-error">{sectionError}</div>}

            <div className="cp-section-title" style={{ marginTop: 4 }}>
              Milestones ({milestones.length})
              <button className="cd-new-inv-btn" onClick={() => setEditor({ type: 'milestones', initial: null })}>+ Add Milestone</button>
            </div>
            {milestones.length > 0 ? (
              <div className="cd-phase-list">
                {milestones.map(item => (
                  <div key={item.id} className="cd-phase-row">
                    <div className="cd-phase-main">
                      <div className="cd-phase-top">
                        <div className="cd-phase-title">{item.title}</div>
                        <PhaseStatusChip status={item.status} />
                      </div>
                      <div className="cd-phase-meta">
                        Due {formatDate(item.target_date)}{item.acceptance_date ? ` · Accepted ${formatDate(item.acceptance_date)}` : ''}
                      </div>
                      {item.description && <div className="cd-phase-note">{item.description}</div>}
                    </div>
                    <div className="cd-phase-side">
                      <div className="cd-phase-figure">{formatMoney(item.amount, contractCurrency)}</div>
                      {renderPhaseActions('milestones', item, 'Milestone')}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="cd-no-inv">{PHASE_EMPTY.milestones}</p>}

            <div className="cp-section-title">
              Deliverables ({deliverables.length})
              <button className="cd-new-inv-btn" onClick={() => setEditor({ type: 'deliverables', initial: null })}>+ Add Deliverable</button>
            </div>
            {deliverables.length > 0 ? (
              <div className="cd-phase-list">
                {deliverables.map(item => (
                  <div key={item.id} className="cd-phase-row">
                    <div className="cd-phase-main">
                      <div className="cd-phase-top">
                        <div className="cd-phase-title">{item.title}</div>
                        <div className="cd-phase-tags">
                          {item.milestone_title && <span className="cd-phase-tag">{item.milestone_title}</span>}
                          <PhaseStatusChip status={item.status} />
                        </div>
                      </div>
                      <div className="cd-phase-meta">
                        Due {formatDate(item.due_date)}{item.reviewer_name ? ` · Reviewer ${item.reviewer_name}` : ''}{amount(item.revision_count) > 0 ? ` · Revisions ${item.revision_count}` : ''}
                      </div>
                      {(item.description || item.notes) && <div className="cd-phase-note">{item.description || item.notes}</div>}
                    </div>
                    <div className="cd-phase-side">
                      {item.external_url ? <a className="cd-phase-link" href={item.external_url} target="_blank" rel="noreferrer">Open</a> : <span className="cd-phase-muted">No link</span>}
                      {renderPhaseActions('deliverables', item, 'Deliverable')}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="cd-no-inv">{PHASE_EMPTY.deliverables}</p>}

            <div className="cp-section-title">
              Document Register ({documents.length})
              <button className="cd-new-inv-btn" onClick={() => setEditor({ type: 'documents', initial: null })}>+ Add Document</button>
            </div>
            {documents.length > 0 ? (
              <div className="cd-phase-list">
                {documents.map(item => (
                  <div key={item.id} className="cd-phase-row">
                    <div className="cd-phase-main">
                      <div className="cd-phase-top">
                        <div className="cd-phase-title">{item.title}</div>
                        <div className="cd-phase-tags">
                          {item.doc_type && <span className="cd-phase-tag">{item.doc_type}</span>}
                          {item.version && <span className="cd-phase-tag">{item.version}</span>}
                          {item.is_required ? <span className="cd-phase-tag required">Required</span> : null}
                          <PhaseStatusChip status={item.status} />
                        </div>
                      </div>
                      <div className="cd-phase-meta">Document Date {formatDate(item.document_date)}</div>
                      {item.notes && <div className="cd-phase-note">{item.notes}</div>}
                    </div>
                    <div className="cd-phase-side">
                      {item.external_url ? <a className="cd-phase-link" href={item.external_url} target="_blank" rel="noreferrer">Open</a> : <span className="cd-phase-muted">No link</span>}
                      {renderPhaseActions('documents', item, 'Document')}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="cd-no-inv">{PHASE_EMPTY.documents}</p>}

            <div className="cp-section-title">
              Amendments &amp; Variations ({amendments.length})
              <button className="cd-new-inv-btn" onClick={() => setEditor({ type: 'amendments', initial: null })}>+ Add Amendment</button>
            </div>
            {amendments.length > 0 ? (
              <div className="cd-phase-list">
                {amendments.map(item => (
                  <div key={item.id} className="cd-phase-row">
                    <div className="cd-phase-main">
                      <div className="cd-phase-top">
                        <div className="cd-phase-title">{item.amendment_number || humanize(item.amendment_type)}</div>
                        <div className="cd-phase-tags">
                          <span className="cd-phase-tag">{humanize(item.amendment_type)}</span>
                          <PhaseStatusChip status={item.status} />
                        </div>
                      </div>
                      <div className="cd-phase-meta">
                        Effective {formatDate(item.effective_date)}{item.approval_date ? ` · Approved ${formatDate(item.approval_date)}` : ''}
                      </div>
                      <div className="cd-phase-note">{item.summary}</div>
                    </div>
                    <div className="cd-phase-side">
                      <div className="cd-phase-figure">{formatMoney(item.value_delta, contractCurrency)}</div>
                      <div className="cd-phase-muted">{item.days_delta ? `${item.days_delta} days` : 'No extension'}</div>
                      {renderPhaseActions('amendments', item, 'Amendment')}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="cd-no-inv">{PHASE_EMPTY.amendments}</p>}

            <div className="cp-section-title" style={{ marginTop: 8 }}>
              Invoices ({contract.invoice_count || 0})
              <button className="cd-new-inv-btn" onClick={() => onNewInvoice(contract)}>+ New Invoice from this Contract</button>
            </div>
            {contract.invoices?.length > 0 ? (
              <div className="cd-inv-list">
                {contract.invoices.map(inv => (
                  <div key={inv.id} className="cd-inv-row">
                    <span className="cd-inv-num">{inv.invoice_number || inv.invoiceNumber}</span>
                    <span className="cd-inv-date">{formatDate(inv.invoice_date || inv.invoiceDate)}</span>
                    <span className="cd-inv-total">{formatMoney(inv.total, inv.currency || contractCurrency)}</span>
                    {onLoadInvoice && (
                      <button
                        className="cd-inv-open-btn"
                        type="button"
                        onClick={() => handleOpenInvoice(inv)}
                      >
                        Open
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="cd-no-inv">No invoices yet - create one from this contract.</p>}
          </div>
        </div>
      </div>

      {editor && (
        <PhaseRecordModal
          contract={contract}
          type={editor.type}
          initial={editor.initial}
          onClose={() => setEditor(null)}
          onSaved={async () => {
            setEditor(null);
            await onReload(contract.id);
          }}
        />
      )}
    </>
  );
}

export default function Contracts({ onNewInvoice, onLoadInvoice }) {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { loadContracts(); }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const [list, summary] = await Promise.all([contractsAPI.list(), contractsAPI.stats()]);
      setContracts(list);
      setStats(summary);
    } catch {
      // Keep UI stable if the API is temporarily unavailable.
    } finally {
      setLoading(false);
    }
  };

  const attachFallbackInvoices = async (contract) => {
    if (!contract) return contract;
    if (Array.isArray(contract.invoices) && contract.invoices.length > 0) return contract;
    if (!(contract.invoice_count > 0)) return { ...contract, invoices: contract.invoices || [] };

    try {
      const allInvoices = await invoicesAPI.list();
      const matched = allInvoices.filter(inv => matchesInvoiceToContract(inv, contract));
      return {
        ...contract,
        invoices: matched,
        invoice_count: matched.length,
        invoiced_amount: matched.length
          ? matched.reduce((sum, inv) => sum + amount(inv.total), 0)
          : 0,
      };
    } catch {
      return { ...contract, invoices: contract.invoices || [] };
    }
  };

  const openDetail = async (contract) => {
    try {
      const fresh = await contractsAPI.get(contract.id);
      setDetail(await attachFallbackInvoices(fresh));
    } catch {
      setDetail(await attachFallbackInvoices(contract));
    }
  };

  const reloadDetail = async (contractId) => {
    try {
      const fresh = await contractsAPI.get(contractId);
      setDetail(await attachFallbackInvoices(fresh));
      await loadContracts();
    } catch {
      await loadContracts();
    }
  };

  const handleSave = (saved, isEdit) => {
    if (isEdit) {
      setContracts(prev => prev.map(c => (c.id === saved.id ? { ...c, ...saved } : c)));
      if (detail?.id === saved.id) setDetail(prev => ({ ...prev, ...saved }));
    } else {
      setContracts(prev => [{ ...saved, invoice_count: 0, invoiced_amount: 0 }, ...prev]);
    }
    setShowForm(false);
    setEditData(null);
    loadContracts();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract? Existing invoices will lose the link, and Phase 1 execution records will be removed.')) return;
    try {
      await contractsAPI.remove(id);
      setContracts(prev => prev.filter(c => c.id !== id));
      if (detail?.id === id) setDetail(null);
      loadContracts();
    } catch {
      // Keep current state if delete fails.
    }
  };

  const handleDetailNewInvoice = async (contract) => {
    const fresh = await contractsAPI.get(contract.id).catch(() => contract);
    setDetail(null);
    onNewInvoice({
      ...fresh,
      invoices: undefined,
      milestones: undefined,
      deliverables: undefined,
      documents: undefined,
      amendments: undefined,
    });
  };

  const filtered = contracts
    .filter(c => statusFilter === 'all' || c.status === statusFilter)
    .filter(c => !search || [
      c.project_name,
      c.client_name,
      c.contract_number,
      c.donor_name,
      c.project_reference,
      c.funding_reference,
    ].some(v => v?.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="contracts-page">
      <div className="ct-topbar">
        <div>
          <h1 className="ct-title">Contracts</h1>
          <p className="ct-sub">Manage client agreements, donor metadata, and execution records</p>
        </div>
        <button className="ct-add-btn" onClick={() => { setEditData(null); setShowForm(true); }}>
          + New Contract
        </button>
      </div>

      {stats && (
        <div className="ct-stats">
          {[
            ['cst-stat-blue', 'Active', stats.active_contracts],
            ['cst-stat-navy', 'Total Contracts', stats.total_contracts],
            ['cst-stat-green', 'Active Value', `₹ ${fmt(stats.active_value)}`],
            ['cst-stat-amber', 'Total Contracted', `₹ ${fmt(stats.total_contract_value)}`],
            ['cst-stat-purple', 'Total Invoiced', `₹ ${fmt(stats.total_invoiced)}`],
          ].map(([cls, label, val]) => (
            <div key={label} className={`ct-stat-card ${cls}`}>
              <div className="ct-stat-val">{val}</div>
              <div className="ct-stat-lbl">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="ct-filters">
        <div className="ct-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="ct-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search project, client, donor, contract #, project ref"
          />
        </div>
        <div className="ct-status-tabs">
          {['all', 'active', 'draft', 'completed', 'expired', 'terminated'].map(s => (
            <button
              key={s}
              className={`ct-status-tab${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : STATUS_META[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="ct-loading">Loading contracts...</div>
      ) : filtered.length === 0 ? (
        <div className="ct-empty">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2">
            <path d="M9 17H7A5 5 0 0 1 7 7h2" /><path d="M15 7h2a5 5 0 0 1 0 10h-2" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
          <p>{contracts.length === 0 ? 'No contracts yet. Create your first contract.' : 'No contracts match the current filter.'}</p>
          {contracts.length === 0 && (
            <button className="ct-add-btn" onClick={() => { setEditData(null); setShowForm(true); }}>
              + Create First Contract
            </button>
          )}
        </div>
      ) : (
        <div className="ct-list">
          {filtered.map(c => {
            const contractValue = amount(c.current_contract_value) || amount(c.contract_value);
            const invoicedAmount = amount(c.invoiced_amount);
            const pct = contractValue > 0 ? Math.min((invoicedAmount / contractValue) * 100, 100) : 0;
            const remaining = contractValue - invoicedAmount;
            const isExpiring = c.end_date && new Date(c.end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && c.status === 'active';

            return (
              <div key={c.id} className="ct-card" onClick={() => openDetail(c)}>
                <div className="ct-card-header">
                  <div className="ct-card-main">
                    <div className="ct-card-headline">
                      <div className="ct-card-flags">
                        <span className={`cst-badge ${STATUS_META[c.status]?.color || 'cst-draft'}`}>
                          {STATUS_META[c.status]?.label || c.status}
                        </span>
                        {isExpiring && <span className="cst-badge cst-expiring">Expiring Soon</span>}
                      </div>
                      <div className="ct-card-actions" onClick={e => e.stopPropagation()}>
                        <button className="ct-act-btn" onClick={() => onNewInvoice(c)} title="New invoice from this contract">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                            <line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" />
                          </svg>
                          New Invoice
                        </button>
                        <button className="ct-act-edit" onClick={() => { setEditData(c); setShowForm(true); }} title="Edit">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button className="ct-act-del" onClick={() => handleDelete(c.id)} title="Delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h3 className="ct-card-title">{c.project_name}</h3>
                    <div className="ct-card-client">
                      <div className="ct-card-client-line">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>{c.client_name}</span>
                      </div>
                      {c.contact_person && <div className="ct-attn">Attn: {c.contact_person}</div>}
                    </div>
                    {c.contract_number && <div className="ct-card-num">#{c.contract_number}</div>}
                    {(c.donor_name || c.project_reference || c.selection_method) && (
                      <div className="ct-card-submeta">
                        {[c.donor_name, c.project_reference, c.selection_method].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div className="ct-card-submeta execution">
                      {`${c.milestone_count || 0} milestones · ${c.deliverable_count || 0} deliverables · ${c.document_count || 0} docs · ${c.amendment_count || 0} amendments`}
                    </div>
                  </div>
                </div>

                <div className="ct-card-bottom">
                  <div className="ct-card-values">
                    <div className="ct-mini-val"><span>Ceiling</span><strong>{formatMoney(contractValue, c.billing_currency || 'INR')}</strong></div>
                    <div className="ct-mini-val"><span>Invoiced</span><strong className="invoiced-color">{formatMoney(invoicedAmount, c.billing_currency || 'INR')}</strong></div>
                    <div className="ct-mini-val"><span>Remaining</span><strong className={remaining < 0 ? 'overrun-color' : ''}>{formatMoney(remaining, c.billing_currency || 'INR')}</strong></div>
                    <div className="ct-mini-val"><span>Invoices</span><strong>{c.invoice_count}</strong></div>
                  </div>
                  {contractValue > 0 && (
                    <div className="ct-mini-progress">
                      <div className="ct-mini-bar" style={{ width: `${pct}%` }} />
                    </div>
                  )}

                  {(c.start_date || c.end_date) && (
                    <div className="ct-card-dates">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      {c.start_date && formatDate(c.start_date)}
                      {c.start_date && c.end_date && ' -> '}
                      {c.end_date && formatDate(c.end_date)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ContractPanel
          initial={editData}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditData(null); }}
        />
      )}

      {detail && (
        <ContractDetail
          contract={detail}
          onClose={() => setDetail(null)}
          onNewInvoice={handleDetailNewInvoice}
          onLoadInvoice={onLoadInvoice}
          onEdit={() => { setEditData(detail); setDetail(null); setShowForm(true); }}
          onReload={reloadDetail}
        />
      )}
    </div>
  );
}
